import { useState } from 'react';
import type { Invoice } from '../../shared/api';
import { Banner, Button, DataTable, Field, Input, Modal, Pill, Td } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { fmtDate, money, todayISO } from '../../lib/format';
import type { TabProps } from './types';

export default function InvoicesTab({ job, reload }: TabProps) {
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [draft, setDraft] = useState({ amount: '', description: '', paymentTermsDays: '30' });
  const [payment, setPayment] = useState({ amount: '', receivedDate: todayISO(), cleared: false, isFinal: false });

  const submitInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/jobs/${job.id}/invoices`, {
        amount: Number(draft.amount),
        description: draft.description,
        paymentTermsDays: Number(draft.paymentTermsDays),
      });
      setAdding(false);
      setDraft({ amount: '', description: '', paymentTermsDays: '30' });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the invoice');
    } finally {
      setBusy(false);
    }
  };

  const submitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paying) return;
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/jobs/${job.id}/invoices/${paying.id}/payment`, {
        amount: Number(payment.amount),
        receivedDate: payment.receivedDate,
        cleared: payment.cleared,
        isFinal: payment.isFinal,
      });
      setPaying(null);
      setPayment({ amount: '', receivedDate: todayISO(), cleared: false, isFinal: false });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the payment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">
          {money(job.balance.paid)} paid of {money(job.balance.invoiced)} invoiced ·{' '}
          <span className={job.balance.balance > 0 ? 'text-watch' : 'text-ok'}>{money(job.balance.balance)} outstanding</span>
        </p>
        <Button onClick={() => setAdding(true)}>Add invoice</Button>
      </div>

      {error && <Banner tone="urgent">{error}</Banner>}

      <DataTable head={['#', 'Description', 'Amount', 'Paid', 'Due', 'Status', '']} empty="No invoices on this job yet.">
        {job.invoices.map((inv) => (
          <tr key={inv.id} className="border-b border-border/60">
            <Td className="text-muted">{inv.invoiceNumber}</Td>
            <Td className="font-medium">{inv.description ?? '—'}</Td>
            <Td>{money(inv.amount)}</Td>
            <Td className="text-muted">{money(inv.paidAmount)}</Td>
            <Td className="text-muted">{fmtDate(inv.dueDate)}</Td>
            <Td>
              <Pill tone={inv.status === 'paid' ? 'ok' : inv.status === 'overdue' ? 'urgent' : 'watch'}>{inv.status}</Pill>
            </Td>
            <Td>
              <Button variant="secondary" onClick={() => setPaying(inv)} className="px-3 py-1 text-xs">
                Record payment
              </Button>
            </Td>
          </tr>
        ))}
      </DataTable>

      {adding && (
        <Modal title="Add invoice" onClose={() => setAdding(false)}>
          <form onSubmit={submitInvoice} className="space-y-4">
            <Field label="Amount">
              <Input required type="number" min="1" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
            </Field>
            <Field label="Description">
              <Input required value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Progress billing #2 — rough-in complete" />
            </Field>
            <Field label="Payment terms (days)">
              <Input required type="number" min="1" value={draft.paymentTermsDays} onChange={(e) => setDraft({ ...draft, paymentTermsDays: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Add invoice'}</Button>
            </div>
          </form>
        </Modal>
      )}

      {paying && (
        <Modal title={`Record payment — invoice #${paying.invoiceNumber}`} onClose={() => setPaying(null)}>
          <form onSubmit={submitPayment} className="space-y-4">
            <Field label="Amount received">
              <Input required type="number" min="0.01" step="0.01" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
            </Field>
            <Field label="Date received">
              <Input required type="date" value={payment.receivedDate} onChange={(e) => setPayment({ ...payment, receivedDate: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={payment.cleared} onChange={(e) => setPayment({ ...payment, cleared: e.target.checked })} />
              Funds have cleared
            </label>
            <p className="-mt-2 text-xs text-dim">Uncleared payments only support a conditional waiver.</p>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={payment.isFinal} onChange={(e) => setPayment({ ...payment, isFinal: e.target.checked })} />
              This is the final payment
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setPaying(null)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Record payment'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
